<script setup lang="ts">
import { computed, nextTick, onMounted, ref, type PropType } from 'vue';
import { inputProps } from 'naive-ui';
import GaiaInput from './GaiaInput.vue';

/**
 * Edit-in-place: the input a label turns into when a row is renamed.
 *
 * Four sites had grown their own copy of this — the chat title, the chat
 * history rows, the review analyses, the project tag rows — and they disagreed
 * on the fiddly parts. Only one handled Escape. Each guarded the double commit
 * differently, because Enter also blurs, so a naive "commit on both" fires
 * twice. And each re-derived focus-on-appear through a template or function
 * ref. All of that is here now, once.
 *
 * Mount it (a `v-if` on "is this row being renamed") rather than toggling a
 * prop: it focuses itself on mount, which is the moment the row became
 * editable.
 */
const props = defineProps({
  ...inputProps,
  /** The text as it stands. Commit is suppressed when nothing changed. */
  value: {
    type: String as PropType<string | null>,
    default: '',
  },
  /** Put the caret at the end instead of selecting what is there. */
  selectOnFocus: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  /** The trimmed text, guaranteed non-empty and different from `value`. */
  commit: [value: string];
  /** Escape, or a blur with nothing worth saving. */
  cancel: [];
}>();

const draft = ref(props.value ?? '');
/**
 * Typed by what GaiaInput exposes rather than `InstanceType<typeof GaiaInput>`,
 * which resolves to `any` in eslint's type program (and an `any | null` ref is
 * what it then flags).
 */
const inputRef = ref<{ focus: () => void; select: () => void } | null>(null);

/**
 * Enter commits and then blurs, and blur commits too — so the first outcome
 * wins and the rest are ignored. Without this the rename fires twice, which
 * each of the old copies worked around in its own way.
 */
let settled = false;

onMounted(() => {
  void nextTick(() => {
    inputRef.value?.focus();
    if (props.selectOnFocus) inputRef.value?.select();
  });
});

function commit(): void {
  if (settled) return;
  settled = true;
  const next = draft.value.trim();
  // Empty, or unchanged, is a cancel: there is nothing for a caller to save,
  // and every old copy had to remember to check both.
  if (!next || next === (props.value ?? '').trim()) {
    emit('cancel');
    return;
  }
  emit('commit', next);
}

function cancel(): void {
  if (settled) return;
  settled = true;
  emit('cancel');
}

const inputBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.selectOnFocus;
  delete bindings.value;
  delete bindings.onKeydown;
  delete bindings.onBlur;
  return bindings;
});
</script>

<template>
  <gaia-input
    ref="inputRef"
    v-bind="inputBindings"
    :value="draft"
    @update:value="(next: string) => (draft = next)"
    @blur="commit"
    @keydown.enter.prevent="commit"
    @keydown.esc.prevent="cancel"
    @click.stop
  />
</template>
